import { RefObject } from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import ArrowsSynchronize from '@/components/atom/icon/ArrowsSynchronize';
import ReportSelectModal from '@/components/molecules/Modal/SelectModal/ReportSelectModal';
import useAuthModal from '@/hooks/useAuthModal';
import usePostReportModal, { PostReportDataType } from '@/hooks/usePostReportModal';
import { tokenState } from '@/recoil/atoms/common/auth';
import { BoardPostDetail, CommunityUser } from '@/types/community';

import PostMultipleComment from './PostMultipleComment';

interface Props {
  commentsRef: RefObject<HTMLUListElement>;
  boardPostDetail: BoardPostDetail;
  refreshPost: () => void;
  setLikeCommentStatus: (e: React.MouseEvent, boardCommentPid: string, isLike: boolean) => void;
  userProfile?: CommunityUser;
  onShowBannedUserModal: () => void;
}

const PostComments = ({ commentsRef, boardPostDetail, refreshPost, setLikeCommentStatus, userProfile }: Props) => {
  const token = useRecoilValue(tokenState);
  const { setShowAuthModal } = useAuthModal();
  const { showReportModal, onShowReportModal, onCloseReportModal, reportData, reportItems } = usePostReportModal();

  const onClickRefresh = () => {
    refreshPost();
  };

  // 질문 신고하기
  const onShowModal = (e: React.MouseEvent, data: PostReportDataType) => {
    e.stopPropagation();

    if (!token) return setShowAuthModal(true);
    onShowReportModal(data);
  };

  return (
    <Container>
      <TitleWrapper>
        <Title>
          댓글
          <CommentCount>{boardPostDetail.boardCommentCount}개</CommentCount>
        </Title>
        <Refresh onClick={onClickRefresh}>
          <ArrowsSynchronize />
          새로고침
        </Refresh>
      </TitleWrapper>
      <Comments ref={commentsRef}>
        <PostMultipleComment
          comments={boardPostDetail.boardComments}
          setLikeCommentStatus={setLikeCommentStatus}
          onShowReportModal={onShowModal}
          isAdmin={userProfile?.isAdmin ?? false}
        />
      </Comments>

      {reportItems && (
        <ReportSelectModal
          isVisible={showReportModal}
          onClose={onCloseReportModal}
          selectList={reportItems}
          title={reportData?.isAdmin ? '관리하기' : reportData?.isMine ? '댓글 관리' : '신고하기'}
        />
      )}
    </Container>
  );
};

export default PostComments;

const Container = styled.section`
  padding-top: 32px;
  ${({ theme }) => `
    background: ${theme.color.white};
  `};
`;

const TitleWrapper = styled.div`
  padding: 0 24px 8px;
  display: flex;
  justify-content: space-between;
`;

const Title = styled.div`
  display: flex;
  align-items: center;

  ${({ theme }) => `
    ${theme.typography.UI18B};
    color: ${theme.color.gray800};
  `};
`;

const CommentCount = styled.div`
  padding: 3px 8px;
  margin-left: 8px;
  border-radius: 4px;

  ${({ theme }) => `
    ${theme.typography.UI12M};
    color: ${theme.color.blue500};
    background: ${theme.color.blue100};
  `};
`;

const Refresh = styled.div`
  display: flex;
  align-items: center;
  svg {
    margin-right: 6px;
  }

  ${({ theme }) => `
    ${theme.typography.UI14M};
    color: ${theme.color.gray300};
  `};
`;
const Comments = styled.ul`
  padding-bottom: 16px;
`;
